import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import type {
  AdminActivityLogItem,
  AdminImeiCheckRow,
  AdminTransactionRow,
  AdminUserDetail,
  AdminUserListingRow,
  AdminUserRow,
} from './adminTypes'
import { useAdminUi } from './AdminUiProvider'
import { formatWhen } from './adminUtils'

type Props = {
  token: string
  user: AdminUserRow
  onClose: () => void
  onUpdated: (user: AdminUserRow) => void
}

type DrawerTab = 'profile' | 'listings' | 'imei' | 'transactions' | 'activity'
type ListingFilter = 'all' | 'active' | 'sold'

export default function AdminUserDetailDrawer({ token, user, onClose, onUpdated }: Props) {
  const { t, i18n } = useTranslation()
  const { toast, confirm } = useAdminUi()
  const [tab, setTab] = useState<DrawerTab>('profile')
  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  const [listings, setListings] = useState<AdminUserListingRow[]>([])
  const [imeiChecks, setImeiChecks] = useState<AdminImeiCheckRow[]>([])
  const [transactions, setTransactions] = useState<AdminTransactionRow[]>([])
  const [activity, setActivity] = useState<AdminActivityLogItem[]>([])
  const [listingFilter, setListingFilter] = useState<ListingFilter>('all')
  const [loading, setLoading] = useState(true)
  const [tabLoading, setTabLoading] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const loadDetail = useCallback(() => {
    setLoading(true)
    setError('')
    adminAPI
      .getUser(token, user.id)
      .then((res) => setDetail(res.data as AdminUserDetail))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, user.id])

  const loadListings = useCallback(() => {
    setTabLoading(true)
    adminAPI
      .userListings(token, user.id, { sold: listingFilter, limit: 100 })
      .then((res) => setListings((res.data?.items as AdminUserListingRow[]) ?? []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setTabLoading(false))
  }, [token, user.id, listingFilter])

  const loadImei = useCallback(() => {
    setTabLoading(true)
    adminAPI
      .userImeiChecks(token, user.id, { limit: 100 })
      .then((res) => setImeiChecks((res.data?.items as AdminImeiCheckRow[]) ?? []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setTabLoading(false))
  }, [token, user.id])

  const loadTransactions = useCallback(() => {
    setTabLoading(true)
    adminAPI
      .userTransactions(token, user.id, { limit: 100 })
      .then((res) => setTransactions((res.data?.items as AdminTransactionRow[]) ?? []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setTabLoading(false))
  }, [token, user.id])

  const loadActivity = useCallback(() => {
    setTabLoading(true)
    adminAPI
      .userActivityLog(token, user.id, { limit: 100 })
      .then((res) => setActivity((res.data?.items as AdminActivityLogItem[]) ?? []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setTabLoading(false))
  }, [token, user.id])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  useEffect(() => {
    if (tab === 'listings') loadListings()
    if (tab === 'imei') loadImei()
    if (tab === 'transactions') loadTransactions()
    if (tab === 'activity') loadActivity()
  }, [tab, loadListings, loadImei, loadTransactions, loadActivity])

  async function toggleActive() {
    if (!detail || detail.role === 'admin') return
    const blocking = detail.is_active
    const ok = await confirm({
      title: blocking ? t('admin.blockUser') : t('admin.unblockUser'),
      message: blocking ? detail.email : detail.email,
      confirmLabel: blocking ? t('admin.blockUser') : t('admin.unblockUser'),
      danger: blocking,
    })
    if (!ok) return
    setBusy(true)
    adminAPI
      .updateUser(token, detail.id, { is_active: !detail.is_active })
      .then((res) => {
        const updated = res.data as AdminUserRow
        setDetail((prev) => (prev ? { ...prev, ...updated } : prev))
        onUpdated(updated)
        toast.success(blocking ? t('admin.toast.userBlocked') : t('admin.toast.userUnblocked'))
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  const d = detail ?? user

  return (
    <div className="vc-admin-drawer" role="dialog" aria-modal="true">
      <button type="button" className="vc-admin-drawer__backdrop" onClick={onClose} aria-label="Close" />
      <aside className="vc-admin-drawer__panel">
        <header className="vc-admin-drawer__head">
          <div>
            <h2>{d.full_name || d.email}</h2>
            {d.full_name ? <p className="vc-admin-muted">{d.email}</p> : null}
          </div>
          <button type="button" className="vc-admin-drawer__close" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="vc-admin-drawer__tabs">
          {(['profile', 'listings', 'imei', 'transactions', 'activity'] as DrawerTab[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`vc-admin-drawer__tab${tab === key ? ' is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {t(`admin.userTab.${key}`)}
            </button>
          ))}
        </div>

        {error ? <p className="vc-admin-error">{error}</p> : null}
        {loading && tab === 'profile' ? <p className="meta-text">{t('common.loading')}</p> : null}
        {tabLoading && tab !== 'profile' ? <p className="meta-text">{t('common.loading')}</p> : null}

        {tab === 'profile' && !loading && detail ? (
          <>
            <section className="vc-admin-drawer__section">
              <h3>{t('admin.userProfile')}</h3>
              <dl className="vc-admin-dl">
                <div>
                  <dt>{t('admin.role')}</dt>
                  <dd>{detail.role}</dd>
                </div>
                <div>
                  <dt>{t('admin.status')}</dt>
                  <dd>
                    <span className={`vc-admin-badge${detail.is_active ? '' : ' vc-admin-badge--warn'}`}>
                      {detail.is_active ? t('admin.statusActive') : t('admin.statusBlocked')}
                    </span>
                  </dd>
                </div>
                {detail.created_at ? (
                  <div>
                    <dt>{t('admin.registeredAt')}</dt>
                    <dd>{new Date(detail.created_at).toLocaleDateString()}</dd>
                  </div>
                ) : null}
                {detail.seller_verification_status ? (
                  <div>
                    <dt>{t('admin.verification')}</dt>
                    <dd>{detail.seller_verification_status}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
            <section className="vc-admin-drawer__section">
              <h3>{t('admin.userStats')}</h3>
              <div className="vc-admin-inline-stats">
                <span>
                  {t('admin.listingsActive')}: <strong>{detail.listings_active}</strong>
                </span>
                <span>
                  {t('admin.offers')}: <strong>{detail.offers_count}</strong>
                </span>
                <span>
                  {t('admin.imeiReports')}: <strong>{detail.imei_reports_count}</strong>
                </span>
              </div>
            </section>
            {detail.role !== 'admin' ? (
              <div className="vc-admin-drawer__actions">
                <button
                  type="button"
                  className={`vc-admin-btn${detail.is_active ? ' vc-admin-btn--danger' : ' vc-admin-btn--primary'}`}
                  disabled={busy}
                  onClick={toggleActive}
                >
                  {detail.is_active ? t('admin.blockUser') : t('admin.unblockUser')}
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        {tab === 'listings' ? (
          <>
            <div className="vc-admin-drawer__tabs vc-admin-drawer__tabs--nested">
              {(['all', 'active', 'sold'] as ListingFilter[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`vc-admin-drawer__tab${listingFilter === key ? ' is-active' : ''}`}
                  onClick={() => setListingFilter(key)}
                >
                  {t(`admin.listingsFilter.${key}`)}
                </button>
              ))}
            </div>
            {listings.length === 0 ? (
              <p className="meta-text">{t('admin.noListings')}</p>
            ) : (
              <ul className="vc-admin-listing-cards">
                {listings.map((listing) => (
                  <li key={listing.id}>
                    <button
                      type="button"
                      className="vc-admin-listing-card"
                      onClick={() => window.open(`/listing/${listing.id}`, '_blank', 'noopener,noreferrer')}
                    >
                      <span className="vc-admin-listing-card__title">
                        {listing.manufacturer} {listing.model_name}
                      </span>
                      <span className="vc-admin-listing-card__meta">
                        €{listing.price.toFixed(0)} · {listing.condition}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}

        {tab === 'imei' ? (
          imeiChecks.length === 0 ? (
            <p className="meta-text">{t('admin.imeiNoChecks')}</p>
          ) : (
            <ul className="vc-admin-activity">
              {imeiChecks.map((row) => (
                <li key={row.id} className={row.is_stolen ? 'is-danger' : ''}>
                  <span>{formatWhen(row.created_at, i18n.language)}</span>
                  <strong>
                    {row.brand} {row.model}
                  </strong>
                  <span className="vc-admin-muted">{row.imei_masked}</span>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {tab === 'transactions' ? (
          transactions.length === 0 ? (
            <p className="meta-text">{t('admin.noTransactions')}</p>
          ) : (
            <ul className="vc-admin-activity">
              {transactions.map((row) => (
                <li key={row.id}>
                  <span>{formatWhen(row.created_at, i18n.language)}</span>
                  <strong>€{row.amount.toFixed(0)} · {row.canonical_state}</strong>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {tab === 'activity' ? (
          activity.length === 0 ? (
            <p className="meta-text">{t('admin.activityEmpty')}</p>
          ) : (
            <ol className="vc-admin-timeline">
              {activity.map((item, index) => (
                <li key={`${item.type}-${item.at}-${index}`}>
                  <span className="vc-admin-timeline__time">{formatWhen(item.at, i18n.language)}</span>
                  <strong>{t(`admin.activityLog.${item.type}`, { defaultValue: item.type })}</strong>
                  <span className="vc-admin-muted">{item.label}</span>
                </li>
              ))}
            </ol>
          )
        ) : null}
      </aside>
    </div>
  )
}
